'use client'
import clsx from 'clsx'
import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * The docpace select value-shape aliases. Canonically defined in
 * `@docpace/shared-ts-types` (so non-React code can use them without a
 * circular dependency) and re-exported here so they can be imported directly
 * from the component, e.g.
 * `import { ReactSelect, MultiSelectValue } from '@docpace/shared-react-components'`.
 *
 * `SelectOption` is the general/generic option type this component operates on;
 * `SingleSelectValue<T>` / `MultiSelectValue<T>` are the narrower
 * `{ label: string; value: T }` shapes used across the *Selector components.
 */
export type { SingleSelectValue, MultiSelectValue } from '@docpace/shared-ts-types'

/**
 * A native, Tailwind-styled replacement for the `react-select` package.
 *
 * The prop names and value shapes intentionally mirror `react-select` v5 so
 * that existing `<Select />` call sites (see AppointmentTypeSelector,
 * HeaderReactSelect, FormSelect, etc.) can migrate with minimal changes. It
 * has no third-party dependencies and renders its menu through a portal with
 * `position: fixed` so it never clips inside dialogs or scroll containers.
 */

export interface SelectOption {
    label: React.ReactNode
    value: any
    isDisabled?: boolean
    // allow arbitrary extra data on options (react-select parity)
    [key: string]: any
}

export type SingleValue<Option> = Option | null
export type MultiValue<Option> = readonly Option[]
export type OnChangeValue<
    Option,
    IsMulti extends boolean
> = IsMulti extends true ? MultiValue<Option> : SingleValue<Option>
export type PropsValue<Option, IsMulti extends boolean> = OnChangeValue<
    Option,
    IsMulti
>

export type SelectActionMeta<Option> =
    | { action: 'select-option'; option?: Option; name?: string }
    | { action: 'deselect-option'; option?: Option; name?: string }
    | { action: 'remove-value'; removedValue: Option; name?: string }
    | { action: 'pop-value'; removedValue: Option; name?: string }
    | { action: 'clear'; removedValues: Option[]; name?: string }

export type InputActionMeta =
    | { action: 'input-change' }
    | { action: 'input-blur' }
    | { action: 'menu-close' }
    | { action: 'set-value' }

/**
 * A per-part style override, mirroring `react-select`'s `styles` API. Each
 * function receives the component's default (base) inline styles for that part
 * plus a small state object, and returns the final inline styles, e.g.
 * `control: (base, state) => ({ ...base, height: 44 })`.
 *
 * Note: this component is styled primarily with Tailwind classes, so `base` is
 * usually the small set of dynamic inline styles the component sets itself
 * (positioning, z-index, max-height, …) rather than a full CSS declaration.
 */
export type StyleFn = (
    base: React.CSSProperties,
    state: Record<string, any>
) => React.CSSProperties

export interface ReactSelectStyles {
    container?: StyleFn
    control?: StyleFn
    valueContainer?: StyleFn
    input?: StyleFn
    placeholder?: StyleFn
    singleValue?: StyleFn
    multiValue?: StyleFn
    indicatorsContainer?: StyleFn
    menu?: StyleFn
    menuList?: StyleFn
    option?: StyleFn
}

export interface ReactSelectProps<
    Option extends SelectOption = SelectOption,
    IsMulti extends boolean = false
> {
    options: readonly Option[]
    /** Controlled value. Omit (and optionally use `defaultValue`) for uncontrolled usage. */
    value?: PropsValue<Option, IsMulti>
    defaultValue?: PropsValue<Option, IsMulti>
    onChange?: (
        value: OnChangeValue<Option, IsMulti>,
        actionMeta: SelectActionMeta<Option>
    ) => void
    onInputChange?: (inputValue: string, actionMeta: InputActionMeta) => void
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
    onMenuOpen?: () => void
    onMenuClose?: () => void

    /** Controlled input value (search text). */
    inputValue?: string
    /** Controlled menu open state. */
    menuIsOpen?: boolean

    isMulti?: IsMulti
    isClearable?: boolean
    isSearchable?: boolean
    isDisabled?: boolean
    isLoading?: boolean
    isOptionDisabled?: (option: Option) => boolean

    placeholder?: React.ReactNode
    noOptionsMessage?: (obj: { inputValue: string }) => React.ReactNode
    loadingMessage?: (obj: { inputValue: string }) => React.ReactNode

    name?: string
    id?: string
    inputId?: string
    /** Applied to the outer container, mirroring react-select's `className`. */
    className?: string
    /** Applied to the control (input box) element. */
    classNames?: { control?: string; menu?: string; option?: string }
    /** Per-part inline style overrides, mirroring `react-select`'s `styles` prop. */
    styles?: ReactSelectStyles
    autoFocus?: boolean
    required?: boolean

    openMenuOnFocus?: boolean
    openMenuOnClick?: boolean
    closeMenuOnSelect?: boolean
    hideSelectedOptions?: boolean
    backspaceRemovesValue?: boolean
    tabSelectsValue?: boolean
    escapeClearsValue?: boolean
    blurInputOnSelect?: boolean

    menuPlacement?: 'auto' | 'bottom' | 'top'
    maxMenuHeight?: number
    /**
     * The CSS `position` of the open menu, mirroring `react-select`'s
     * `menuPosition`.
     *
     * - `'fixed'` (this component's default): the menu is positioned relative
     *   to the viewport, so it escapes `overflow`/scroll containers and clipping
     *   ancestors. Combined with the default `menuPortalTarget`, this matches how
     *   `react-select` was used across the app (`menuPosition="fixed"`).
     * - `'absolute'`: the menu is positioned relative to its nearest positioned
     *   ancestor (the select container when not portaled, or the portal target's
     *   document flow when portaled). This is `react-select`'s own default.
     */
    menuPosition?: 'absolute' | 'fixed'
    /**
     * Portal target for the menu.
     * - Omit (default): auto-resolve to the nearest native `<dialog>` ancestor
     *   if there is one — so the menu stays in the browser top layer inside a
     *   modal instead of rendering behind it — otherwise `document.body`.
     * - An element: portal there.
     * - `null`: render inline (no portal).
     */
    menuPortalTarget?: HTMLElement | null
    /**
     * Keep the open menu above surrounding content by giving it a very high
     * `z-index` (9999). Defaults to `true`. Set to `false` to let the menu use
     * natural stacking. Replaces the `react-select` `styles.menuPortal` zIndex
     * override. Use `menuZIndex` for an explicit value (takes precedence).
     */
    alwaysVisible?: boolean
    menuZIndex?: number

    getOptionLabel?: (option: Option) => string
    getOptionValue?: (option: Option) => string
    formatOptionLabel?: (
        option: Option,
        meta: { context: 'menu' | 'value'; inputValue: string }
    ) => React.ReactNode
    /** Return true to keep an option. Pass `null` to disable filtering entirely. */
    filterOption?: ((option: Option, inputValue: string) => boolean) | null

    'aria-label'?: string
    'aria-labelledby'?: string
}

interface MenuPosition {
    left: number
    width: number
    placement: 'bottom' | 'top'
    top?: number
    bottom?: number
    maxHeight: number
}

const MENU_GAP = 4

export function ReactSelect<
    Option extends SelectOption = SelectOption,
    IsMulti extends boolean = false
>(props: ReactSelectProps<Option, IsMulti>) {
    const {
        options,
        value: controlledValue,
        defaultValue,
        onChange,
        onInputChange,
        onBlur,
        onFocus,
        onKeyDown,
        onMenuOpen,
        onMenuClose,
        inputValue: controlledInput,
        menuIsOpen: controlledMenuOpen,
        isMulti,
        isClearable,
        isSearchable = true,
        isDisabled = false,
        isLoading = false,
        isOptionDisabled,
        placeholder = 'Select...',
        noOptionsMessage = () => 'No options',
        loadingMessage = () => 'Loading...',
        name,
        id,
        inputId,
        className,
        classNames,
        styles,
        autoFocus,
        required,
        openMenuOnFocus = false,
        openMenuOnClick = true,
        closeMenuOnSelect = !isMulti,
        hideSelectedOptions = !!isMulti,
        backspaceRemovesValue = true,
        tabSelectsValue = true,
        escapeClearsValue = false,
        blurInputOnSelect,
        menuPlacement = 'auto',
        maxMenuHeight = 300,
        menuPosition = 'fixed',
        menuPortalTarget,
        alwaysVisible = true,
        menuZIndex,
        getOptionLabel,
        getOptionValue,
        formatOptionLabel,
        filterOption,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledby,
    } = props

    const reactId = useId()
    const listboxId = `${id ?? reactId}-listbox`
    const resolvedInputId = inputId ?? `${id ?? reactId}-input`

    const controlRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const menuListRef = useRef<HTMLDivElement>(null)

    // Resolved when the menu opens: the nearest <dialog> ancestor (to stay in
    // the browser top layer inside a native modal) or document.body.
    const [autoPortalTarget, setAutoPortalTarget] = useState<HTMLElement | null>(
        null
    )
    const [isFocused, setIsFocused] = useState(false)
    const [focusedIndex, setFocusedIndex] = useState(0)
    const [menuStyle, setMenuStyle] = useState<MenuPosition | null>(null)

    // -- controlled / uncontrolled state ------------------------------------
    const isValueControlled = controlledValue !== undefined
    const [internalValue, setInternalValue] = useState<
        PropsValue<Option, IsMulti>
    >(
        defaultValue ??
            ((isMulti ? [] : null) as unknown as PropsValue<Option, IsMulti>)
    )
    const value = isValueControlled ? controlledValue : internalValue

    const isInputControlled = controlledInput !== undefined
    const [internalInput, setInternalInput] = useState('')
    const inputValue = isInputControlled ? controlledInput : internalInput

    const isMenuControlled = controlledMenuOpen !== undefined
    const [internalMenuOpen, setInternalMenuOpen] = useState(false)
    const menuOpen = isMenuControlled ? controlledMenuOpen : internalMenuOpen

    // -- helpers -------------------------------------------------------------
    const getVal = useCallback(
        (option: Option): string =>
            getOptionValue ? getOptionValue(option) : String(option.value),
        [getOptionValue]
    )
    const getLabel = useCallback(
        (option: Option): string => {
            if (getOptionLabel) return getOptionLabel(option)
            return typeof option.label === 'string'
                ? option.label
                : String(option.label ?? '')
        },
        [getOptionLabel]
    )
    const optionDisabled = useCallback(
        (option: Option): boolean =>
            isOptionDisabled ? isOptionDisabled(option) : !!option.isDisabled,
        [isOptionDisabled]
    )

    const selectedOptions: Option[] = useMemo(() => {
        if (isMulti) return value ? (value as Option[]).slice() : []
        return value ? [value as Option] : []
    }, [value, isMulti])

    const selectedValues = useMemo(
        () => new Set(selectedOptions.map(getVal)),
        [selectedOptions, getVal]
    )

    const hasValue = selectedOptions.length > 0

    // -- filtered options ----------------------------------------------------
    const filteredOptions: Option[] = useMemo(() => {
        let opts = options.slice()
        if (hideSelectedOptions) {
            opts = opts.filter((o) => !selectedValues.has(getVal(o)))
        }
        if (filterOption === null) return opts
        const trimmed = inputValue.trim().toLowerCase()
        if (filterOption) return opts.filter((o) => filterOption(o, inputValue))
        if (!trimmed) return opts
        return opts.filter((o) => getLabel(o).toLowerCase().includes(trimmed))
    }, [
        options,
        hideSelectedOptions,
        selectedValues,
        filterOption,
        inputValue,
        getVal,
        getLabel,
    ])

    // -- menu open/close -----------------------------------------------------
    const openMenu = useCallback(() => {
        if (isDisabled || menuOpen) return
        if (!isMenuControlled) setInternalMenuOpen(true)
        // focus the currently-selected option, else the first enabled one
        const selectedIdx = filteredOptions.findIndex((o) =>
            selectedValues.has(getVal(o))
        )
        setFocusedIndex(selectedIdx >= 0 ? selectedIdx : 0)
        onMenuOpen?.()
    }, [
        isDisabled,
        menuOpen,
        isMenuControlled,
        filteredOptions,
        selectedValues,
        getVal,
        onMenuOpen,
    ])

    const closeMenu = useCallback(() => {
        if (!menuOpen) return
        if (!isMenuControlled) setInternalMenuOpen(false)
        setMenuStyle(null)
        if (!isInputControlled) setInternalInput('')
        onInputChange?.('', { action: 'menu-close' })
        onMenuClose?.()
    }, [menuOpen, isMenuControlled, isInputControlled, onInputChange, onMenuClose])

    const setInput = useCallback(
        (next: string) => {
            if (!isInputControlled) setInternalInput(next)
            onInputChange?.(next, { action: 'input-change' })
        },
        [isInputControlled, onInputChange]
    )

    const emitChange = useCallback(
        (
            next: OnChangeValue<Option, IsMulti>,
            actionMeta: SelectActionMeta<Option>
        ) => {
            if (!isValueControlled)
                setInternalValue(next as PropsValue<Option, IsMulti>)
            onChange?.(next, { ...actionMeta, name })
        },
        [isValueControlled, onChange, name]
    )

    // -- selection handlers --------------------------------------------------
    const selectOption = useCallback(
        (option: Option) => {
            if (optionDisabled(option)) return
            const alreadySelected = selectedValues.has(getVal(option))

            if (isMulti) {
                let next: Option[]
                let actionMeta: SelectActionMeta<Option>
                if (alreadySelected) {
                    next = selectedOptions.filter(
                        (o) => getVal(o) !== getVal(option)
                    )
                    actionMeta = { action: 'deselect-option', option }
                } else {
                    next = [...selectedOptions, option]
                    actionMeta = { action: 'select-option', option }
                }
                emitChange(
                    next as unknown as OnChangeValue<Option, IsMulti>,
                    actionMeta
                )
            } else {
                emitChange(option as OnChangeValue<Option, IsMulti>, {
                    action: 'select-option',
                    option,
                })
            }

            if (!isInputControlled) setInternalInput('')
            if (closeMenuOnSelect) {
                closeMenu()
                if (blurInputOnSelect ?? !isMulti) inputRef.current?.blur()
            } else {
                inputRef.current?.focus()
            }
        },
        [
            optionDisabled,
            selectedValues,
            getVal,
            isMulti,
            selectedOptions,
            emitChange,
            isInputControlled,
            closeMenuOnSelect,
            closeMenu,
            blurInputOnSelect,
        ]
    )

    const removeValue = useCallback(
        (option: Option, action: 'remove-value' | 'pop-value') => {
            const next = selectedOptions.filter(
                (o) => getVal(o) !== getVal(option)
            )
            emitChange(next as unknown as OnChangeValue<Option, IsMulti>, {
                action,
                removedValue: option,
            })
        },
        [selectedOptions, getVal, emitChange]
    )

    const clearValue = useCallback(() => {
        emitChange(
            (isMulti ? [] : null) as unknown as OnChangeValue<Option, IsMulti>,
            { action: 'clear', removedValues: selectedOptions }
        )
        inputRef.current?.focus()
    }, [emitChange, isMulti, selectedOptions])

    // -- keyboard handling ---------------------------------------------------
    const focusAdjacent = useCallback(
        (direction: 1 | -1) => {
            const count = filteredOptions.length
            if (count === 0) return
            let next = focusedIndex
            for (let i = 0; i < count; i++) {
                next = (next + direction + count) % count
                if (!optionDisabled(filteredOptions[next])) break
            }
            setFocusedIndex(next)
        },
        [filteredOptions, focusedIndex, optionDisabled]
    )

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            onKeyDown?.(event)
            if (event.defaultPrevented || isDisabled) return

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault()
                    if (!menuOpen) openMenu()
                    else focusAdjacent(1)
                    break
                case 'ArrowUp':
                    event.preventDefault()
                    if (!menuOpen) openMenu()
                    else focusAdjacent(-1)
                    break
                case 'Enter':
                    if (menuOpen && filteredOptions[focusedIndex]) {
                        event.preventDefault()
                        selectOption(filteredOptions[focusedIndex])
                    }
                    break
                case 'Tab':
                    if (
                        tabSelectsValue &&
                        menuOpen &&
                        filteredOptions[focusedIndex] &&
                        inputValue
                    ) {
                        event.preventDefault()
                        selectOption(filteredOptions[focusedIndex])
                    }
                    break
                case 'Escape':
                    if (menuOpen) {
                        event.preventDefault()
                        closeMenu()
                    } else if (escapeClearsValue && hasValue) {
                        event.preventDefault()
                        clearValue()
                    }
                    break
                case 'Backspace':
                    if (
                        !inputValue &&
                        backspaceRemovesValue &&
                        hasValue
                    ) {
                        event.preventDefault()
                        if (isMulti) {
                            removeValue(
                                selectedOptions[selectedOptions.length - 1],
                                'pop-value'
                            )
                        } else {
                            clearValue()
                        }
                    }
                    break
                case ' ':
                    if (!inputValue && !menuOpen) {
                        event.preventDefault()
                        openMenu()
                    }
                    break
                default:
                    break
            }
        },
        [
            onKeyDown,
            isDisabled,
            menuOpen,
            openMenu,
            focusAdjacent,
            filteredOptions,
            focusedIndex,
            selectOption,
            tabSelectsValue,
            inputValue,
            closeMenu,
            escapeClearsValue,
            hasValue,
            clearValue,
            backspaceRemovesValue,
            isMulti,
            removeValue,
            selectedOptions,
        ]
    )

    // -- focus / blur --------------------------------------------------------
    const handleFocus = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(true)
            if (openMenuOnFocus) openMenu()
            onFocus?.(event)
        },
        [openMenuOnFocus, openMenu, onFocus]
    )

    const handleBlur = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            // ignore blur that moves focus into the menu (option mousedown)
            if (
                menuRef.current?.contains(event.relatedTarget as Node) ||
                controlRef.current?.contains(event.relatedTarget as Node)
            ) {
                return
            }
            setIsFocused(false)
            closeMenu()
            onBlur?.(event)
        },
        [closeMenu, onBlur]
    )

    const handleControlMouseDown = useCallback(
        (event: React.MouseEvent) => {
            if (isDisabled) return
            // let clicks on interactive children (clear/remove) handle themselves
            const target = event.target as HTMLElement
            if (target.closest('[data-select-clickable]')) return

            if (menuOpen && openMenuOnClick) {
                closeMenu()
            } else if (openMenuOnClick) {
                openMenu()
            }
            // keep focus on the input rather than the div
            if (event.target !== inputRef.current) {
                event.preventDefault()
                inputRef.current?.focus()
            }
        },
        [isDisabled, menuOpen, openMenuOnClick, closeMenu, openMenu]
    )

    // -- menu positioning ----------------------------------------------------
    const usePortal = menuPortalTarget !== null
    const portalTarget =
        menuPortalTarget === null
            ? null
            : menuPortalTarget ?? autoPortalTarget

    const updatePosition = useCallback(() => {
        const control = controlRef.current
        if (!control) return
        const rect = control.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top
        const placement: 'bottom' | 'top' =
            menuPlacement === 'top'
                ? 'top'
                : menuPlacement === 'bottom'
                ? 'bottom'
                : spaceBelow < maxMenuHeight && spaceAbove > spaceBelow
                ? 'top'
                : 'bottom'

        const available =
            placement === 'bottom'
                ? spaceBelow - MENU_GAP - 4
                : spaceAbove - MENU_GAP - 4
        const maxHeight = Math.max(80, Math.min(maxMenuHeight, available))

        if (menuPosition === 'fixed') {
            // viewport-relative coordinates (position: fixed)
            setMenuStyle({
                left: rect.left,
                width: rect.width,
                placement,
                maxHeight,
                ...(placement === 'bottom'
                    ? { top: rect.bottom + MENU_GAP }
                    : { bottom: window.innerHeight - rect.top + MENU_GAP }),
            })
        } else if (usePortal) {
            // position: absolute, portaled → document-relative coordinates
            setMenuStyle({
                left: rect.left + window.scrollX,
                width: rect.width,
                placement,
                maxHeight,
                ...(placement === 'bottom'
                    ? { top: rect.bottom + window.scrollY + MENU_GAP }
                    : {
                          bottom:
                              document.documentElement.scrollHeight -
                              (rect.top + window.scrollY) +
                              MENU_GAP,
                      }),
            })
        } else {
            // position: absolute, inline within the relative container.
            // Offsets are relative to the container (whose box equals the
            // control), so we can position off the control's height.
            setMenuStyle({
                left: 0,
                width: rect.width,
                placement,
                maxHeight,
                ...(placement === 'bottom'
                    ? { top: rect.height + MENU_GAP }
                    : { bottom: rect.height + MENU_GAP }),
            })
        }
    }, [menuPlacement, maxMenuHeight, menuPosition, usePortal])

    useLayoutEffect(() => {
        if (!menuOpen) return undefined
        // Resolve the default portal target when opening: a native <dialog>
        // ancestor uses the browser top layer, so portaling to document.body
        // would render the menu *behind* the modal. Portal into the dialog
        // instead (still escapes overflow thanks to position: fixed).
        if (menuPortalTarget === undefined) {
            const dialog = controlRef.current?.closest(
                'dialog'
            ) as HTMLElement | null
            setAutoPortalTarget(dialog ?? document.body)
        }
        updatePosition()
        const handle = () => updatePosition()
        window.addEventListener('scroll', handle, true)
        window.addEventListener('resize', handle)
        return () => {
            window.removeEventListener('scroll', handle, true)
            window.removeEventListener('resize', handle)
        }
    }, [menuOpen, updatePosition, menuPortalTarget])

    // close on outside click
    useEffect(() => {
        if (!menuOpen) return undefined
        const handleDocMouseDown = (event: MouseEvent) => {
            const target = event.target as Node
            if (
                controlRef.current?.contains(target) ||
                menuRef.current?.contains(target)
            ) {
                return
            }
            setIsFocused(false)
            closeMenu()
        }
        document.addEventListener('mousedown', handleDocMouseDown)
        return () =>
            document.removeEventListener('mousedown', handleDocMouseDown)
    }, [menuOpen, closeMenu])

    // keep the focused option scrolled into view
    useEffect(() => {
        if (!menuOpen) return
        const list = menuListRef.current
        const el = list?.querySelector<HTMLElement>(
            `[data-option-index="${focusedIndex}"]`
        )
        el?.scrollIntoView({ block: 'nearest' })
    }, [menuOpen, focusedIndex, filteredOptions])

    // reset focused option when the filtered list changes
    useEffect(() => {
        if (focusedIndex > filteredOptions.length - 1) {
            setFocusedIndex(filteredOptions.length > 0 ? 0 : 0)
        }
    }, [filteredOptions.length, focusedIndex])

    // -- render helpers ------------------------------------------------------
    // Merge a part's default (base) inline styles with any `styles[part]`
    // override. Returns undefined when there is nothing to apply so we don't
    // emit an empty `style` attribute.
    const styleFor = (
        part: keyof ReactSelectStyles,
        base: React.CSSProperties = {},
        state: Record<string, any> = {}
    ): React.CSSProperties | undefined => {
        const fn = styles?.[part]
        const result = fn ? fn(base, state) : base
        return Object.keys(result).length > 0 ? result : undefined
    }

    const renderOptionLabel = (option: Option, context: 'menu' | 'value') =>
        formatOptionLabel
            ? formatOptionLabel(option, { context, inputValue })
            : option.label

    const chevron = (
        <svg
            className={clsx(
                'h-4 w-4 shrink-0 text-gray-400 transition-transform duration-150 dark:text-gray-500',
                menuOpen && 'rotate-180'
            )}
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden="true"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 8l4 4 4-4"
            />
        </svg>
    )

    const clearIcon = (
        <svg
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden="true"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 6l8 8M14 6l-8 8"
            />
        </svg>
    )

    const spinner = (
        <svg
            className="h-4 w-4 animate-spin text-gray-400 dark:text-gray-500"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
        </svg>
    )

    // -- menu ----------------------------------------------------------------
    // `alwaysVisible` (default) floats the open menu above surrounding content
    // with a high z-index; an explicit `menuZIndex` always wins.
    const menuZIndexValue = menuZIndex ?? (alwaysVisible ? 9999 : undefined)
    const menu =
        menuOpen && menuStyle ? (
            <div
                ref={menuRef}
                className={clsx(
                    'rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-base-100',
                    classNames?.menu
                )}
                style={styleFor(
                    'menu',
                    {
                        // `position` is functional, so it is set inline (not via
                        // a Tailwind class that may not be generated).
                        position:
                            menuPosition === 'fixed' ? 'fixed' : 'absolute',
                        zIndex: menuZIndexValue,
                        left: menuStyle.left,
                        width: menuStyle.width,
                        ...(menuStyle.top !== undefined
                            ? { top: menuStyle.top }
                            : menuStyle.bottom !== undefined
                            ? { bottom: menuStyle.bottom }
                            : {}),
                    },
                    { placement: menuStyle.placement }
                )}
            >
                <div
                    ref={menuListRef}
                    role="listbox"
                    id={listboxId}
                    aria-multiselectable={isMulti || undefined}
                    className="overflow-y-auto py-1"
                    style={styleFor('menuList', {
                        maxHeight: menuStyle.maxHeight,
                        overflowY: 'auto',
                    })}
                >
                    {isLoading ? (
                        <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">
                            {loadingMessage({ inputValue })}
                        </div>
                    ) : filteredOptions.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">
                            {noOptionsMessage({ inputValue })}
                        </div>
                    ) : (
                        filteredOptions.map((option, index) => {
                            const disabled = optionDisabled(option)
                            const selected = selectedValues.has(getVal(option))
                            const focused = index === focusedIndex
                            // single-select keeps the chosen option in the list
                            // (hideSelectedOptions defaults to false), so give it
                            // a distinct highlight + semibold weight
                            const singleSelected = !isMulti && selected
                            return (
                                <div
                                    key={getVal(option) || index}
                                    role="option"
                                    aria-selected={selected}
                                    aria-disabled={disabled || undefined}
                                    data-option-index={index}
                                    className={clsx(
                                        'cursor-default select-none px-3 py-2 text-sm',
                                        disabled &&
                                            'cursor-not-allowed opacity-50',
                                        !disabled &&
                                            !selected &&
                                            focused &&
                                            'bg-primary-50 dark:bg-base-200',
                                        singleSelected &&
                                            'font-semibold text-primary-900 dark:text-primary-100',
                                        singleSelected &&
                                            !focused &&
                                            'bg-primary-100 dark:bg-primary-800',
                                        singleSelected &&
                                            focused &&
                                            'bg-primary-200 dark:bg-primary-700',
                                        selected &&
                                            !singleSelected &&
                                            'bg-primary-100 font-medium text-primary-900 dark:bg-primary-800 dark:text-primary-100',
                                        !disabled &&
                                            !selected &&
                                            !focused &&
                                            'text-gray-900 dark:text-gray-100',
                                        classNames?.option
                                    )}
                                    style={styleFor('option', {}, {
                                        data: option,
                                        isDisabled: disabled,
                                        isSelected: selected,
                                        isFocused: focused,
                                    })}
                                    onMouseEnter={() =>
                                        !disabled && setFocusedIndex(index)
                                    }
                                    onMouseDown={(e) => {
                                        // prevent input blur before click resolves
                                        e.preventDefault()
                                    }}
                                    onClick={() =>
                                        !disabled && selectOption(option)
                                    }
                                >
                                    {renderOptionLabel(option, 'menu')}
                                </div>
                            )
                        })
                    )}
                </div>
            </div>
        ) : null

    // -- value display -------------------------------------------------------
    const showPlaceholder = !hasValue && !inputValue

    return (
        <div
            className={clsx('relative', className)}
            data-disabled={isDisabled || undefined}
            style={styleFor('container', {}, { isDisabled, isFocused })}
        >
            <div
                ref={controlRef}
                onMouseDown={handleControlMouseDown}
                className={clsx(
                    'flex min-h-[38px] w-full items-center gap-1 rounded-md border bg-white px-2 py-1 text-sm transition-colors dark:bg-base-100',
                    isDisabled
                        ? 'cursor-not-allowed border-gray-200 bg-gray-100 opacity-70 dark:border-gray-700 dark:bg-base-200'
                        : 'cursor-text',
                    isFocused
                        ? 'border-primary-500 ring-1 ring-primary-500 dark:border-primary-500'
                        : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500',
                    classNames?.control
                )}
                style={styleFor('control', {}, { isDisabled, isFocused })}
            >
                <div
                    className="flex flex-1 flex-wrap items-center gap-1 overflow-hidden"
                    style={styleFor('valueContainer')}
                >
                    {/* selected value(s) */}
                    {isMulti
                        ? selectedOptions.map((option) => (
                              <span
                                  key={getVal(option)}
                                  className="inline-flex items-center gap-1 rounded bg-primary-100 py-0.5 pl-2 pr-1 text-xs text-primary-800 dark:bg-primary-800 dark:text-primary-100"
                                  style={styleFor('multiValue', {}, {
                                      data: option,
                                  })}
                              >
                                  <span className="truncate">
                                      {renderOptionLabel(option, 'value')}
                                  </span>
                                  {!isDisabled && (
                                      <button
                                          type="button"
                                          data-select-clickable
                                          aria-label={`Remove ${getLabel(
                                              option
                                          )}`}
                                          className="flex items-center rounded text-primary-600 hover:bg-primary-200 hover:text-primary-900 dark:text-primary-200 dark:hover:bg-primary-700"
                                          onMouseDown={(e) =>
                                              e.stopPropagation()
                                          }
                                          onClick={(e) => {
                                              e.stopPropagation()
                                              removeValue(
                                                  option,
                                                  'remove-value'
                                              )
                                          }}
                                      >
                                          <svg
                                              className="h-3 w-3"
                                              viewBox="0 0 20 20"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth={2}
                                              aria-hidden="true"
                                          >
                                              <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  d="M6 6l8 8M14 6l-8 8"
                                              />
                                          </svg>
                                      </button>
                                  )}
                              </span>
                          ))
                        : hasValue &&
                          !inputValue && (
                              <span
                                  className="truncate text-gray-900 dark:text-gray-100"
                                  style={styleFor('singleValue', {}, {
                                      data: selectedOptions[0],
                                  })}
                              >
                                  {renderOptionLabel(
                                      selectedOptions[0],
                                      'value'
                                  )}
                              </span>
                          )}

                    {/* placeholder */}
                    {showPlaceholder && (
                        <span
                            className="pointer-events-none absolute truncate text-gray-400 dark:text-gray-500"
                            style={styleFor('placeholder')}
                        >
                            {placeholder}
                        </span>
                    )}

                    {/* search input */}
                    <input
                        ref={inputRef}
                        id={resolvedInputId}
                        name={name}
                        type="text"
                        role="combobox"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        aria-autocomplete="list"
                        aria-expanded={menuOpen}
                        aria-controls={menuOpen ? listboxId : undefined}
                        aria-label={ariaLabel}
                        aria-labelledby={ariaLabelledby}
                        aria-required={required || undefined}
                        disabled={isDisabled}
                        autoFocus={autoFocus}
                        readOnly={!isSearchable}
                        value={inputValue}
                        onChange={(e) => {
                            setInput(e.target.value)
                            if (!menuOpen) openMenu()
                        }}
                        onKeyDown={handleKeyDown}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        className={clsx(
                            'min-w-[2px] flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 caret-primary-600 outline-none focus:ring-0 dark:text-gray-100',
                            !isSearchable && 'cursor-default caret-transparent',
                            // keep width minimal so chips wrap nicely in multi mode
                            (hasValue || inputValue) && 'w-2'
                        )}
                        style={styleFor('input', { minWidth: '2px' })}
                    />
                </div>

                {/* indicators */}
                <div
                    className="flex items-center gap-0.5"
                    style={styleFor('indicatorsContainer')}
                >
                    {isLoading && spinner}
                    {isClearable && hasValue && !isDisabled && (
                        <button
                            type="button"
                            data-select-clickable
                            aria-label="Clear selection"
                            tabIndex={-1}
                            className="flex items-center rounded p-0.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation()
                                clearValue()
                            }}
                        >
                            {clearIcon}
                        </button>
                    )}
                    <span
                        className="mx-0.5 h-5 w-px self-stretch bg-gray-200 dark:bg-gray-600"
                        aria-hidden="true"
                    />
                    <span className="flex items-center px-0.5">{chevron}</span>
                </div>
            </div>

            {/* hidden input for native form submission */}
            {name && (
                <input
                    type="hidden"
                    name={name}
                    value={selectedOptions.map(getVal).join(',')}
                    required={required}
                />
            )}

            {usePortal
                ? portalTarget && menu && createPortal(menu, portalTarget)
                : menu}
        </div>
    )
}

ReactSelect.displayName = 'ReactSelect'

export default ReactSelect
